import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
} from 'remotion';

/**
 * #312 动态模糊揭示效果 (Dynamic Blur Reveal)
 *
 * 技术：filter: blur 动画 + 透明度变化
 * - 从模糊到清晰的渐进揭示
 * - 多层叠加效果
 * - SVG 滤镜动态控制
 * - Spring 物理动画
 */

export type DynamicBlurRevealCard = {
  title: string;
  icon: string;
  description?: string;
  delay: number;
};

export type DynamicBlurRevealProps = {
  /** 主标题 */
  title?: string;
  /** 底部说明文字 */
  subtitle?: string;
  /** 技术参数说明（留空自动生成） */
  techInfo?: string;
  /** 卡片列表 */
  cards?: DynamicBlurRevealCard[];
  /** 卡片统一描述（当单个卡片未提供 description 时使用） */
  cardDescription?: string;
  /** 背景色 */
  backgroundColor?: string;
  /** 背景光晕颜色（CSS color） */
  glowColor?: string;
  /** 卡片背景色 */
  cardBackgroundColor?: string;
  /** 卡片边框色 */
  cardBorderColor?: string;
  /** 主文字颜色 */
  primaryColor?: string;
  /** 次要文字颜色 */
  secondaryColor?: string;
  /** 提示文字颜色 */
  hintColor?: string;
  /** 初始模糊度（px） */
  maxBlur?: number;
  /** 初始缩放 */
  initialScale?: number;
  /** 卡片初始模糊度（px） */
  cardMaxBlur?: number;
  /** 卡片初始缩放 */
  cardInitialScale?: number;
  /** 卡片初始 Y 偏移 */
  cardInitialY?: number;
  /** 卡片宽度 */
  cardWidth?: number;
  /** 卡片高度 */
  cardHeight?: number;
  /** 卡片间距 */
  cardGap?: number;
  /** Spring 物理动画参数 */
  springConfig?: {
    damping?: number;
    stiffness?: number;
    mass?: number;
  };
};

const DEFAULT_CARDS: DynamicBlurRevealCard[] = [
  { delay: 0, title: '设计', icon: '🎨' },
  { delay: 10, title: '开发', icon: '💻' },
  { delay: 20, title: '部署', icon: '🚀' },
];

export const DynamicBlurReveal: React.FC<DynamicBlurRevealProps> = ({
  title = '动态模糊揭示',
  subtitle = 'filter: blur() 动画 + Spring 物理效果',
  techInfo,
  cards = DEFAULT_CARDS,
  cardDescription = '从模糊到清晰的渐进揭示效果',
  backgroundColor = '#0a0a0a',
  glowColor = 'rgba(59, 130, 246, 0.2)',
  cardBackgroundColor = '#1a1a1a',
  cardBorderColor = '#333',
  primaryColor = '#fff',
  secondaryColor = '#888',
  hintColor = '#666',
  maxBlur = 20,
  initialScale = 1.2,
  cardMaxBlur = 15,
  cardInitialScale = 0.8,
  cardInitialY = 50,
  cardWidth = 500,
  cardHeight = 400,
  cardGap = 40,
  springConfig,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const centerX = width / 2;
  const centerY = height / 2;

  const mergedSpringConfig = {
    damping: springConfig?.damping ?? 15,
    stiffness: springConfig?.stiffness ?? 100,
    mass: springConfig?.mass ?? 1,
  };

  const revealProgress = spring({
    frame,
    fps,
    config: mergedSpringConfig,
  });

  const blurAmount = interpolate(revealProgress, [0, 1], [maxBlur, 0]);
  const opacity = interpolate(revealProgress, [0, 0.3, 1], [0, 0.5, 1]);
  const scale = interpolate(revealProgress, [0, 1], [initialScale, 1]);

  const resolvedTechInfo =
    techInfo ??
    `Blur: ${blurAmount.toFixed(1)}px | Opacity: ${opacity.toFixed(2)} | Scale: ${scale.toFixed(2)}`;

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 背景光晕 */}
      <div
        style={{
          position: 'absolute',
          width: 800,
          height: 800,
          left: centerX - 400,
          top: centerY - 400,
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
          opacity: revealProgress,
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 150,
          fontSize: 70,
          fontWeight: 'bold',
          color: primaryColor,
          textAlign: 'center',
          filter: `blur(${blurAmount}px)`,
          opacity,
          transform: `scale(${scale})`,
        }}
      >
        {title}
      </div>

      {/* 卡片组 */}
      <div
        style={{
          position: 'absolute',
          top: 350,
          display: 'flex',
          gap: cardGap,
        }}
      >
        {cards.map((card, index) => {
          const cardProgress = spring({
            frame: frame - card.delay,
            fps,
            config: mergedSpringConfig,
          });

          const cardBlur = interpolate(cardProgress, [0, 1], [cardMaxBlur, 0]);
          const cardOpacity = interpolate(cardProgress, [0, 0.5, 1], [0, 0.5, 1]);
          const cardScale = interpolate(cardProgress, [0, 1], [cardInitialScale, 1]);
          const cardY = interpolate(cardProgress, [0, 1], [cardInitialY, 0]);

          return (
            <div
              key={index}
              style={{
                width: cardWidth,
                height: cardHeight,
                backgroundColor: cardBackgroundColor,
                borderRadius: 20,
                padding: 40,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                filter: `blur(${cardBlur}px)`,
                opacity: cardOpacity,
                transform: `scale(${cardScale}) translateY(${cardY}px)`,
                border: `2px solid ${cardBorderColor}`,
              }}
            >
              {/* 图标 */}
              <div style={{ fontSize: 80, marginBottom: 30 }}>{card.icon}</div>

              {/* 标题 */}
              <div style={{ fontSize: 40, fontWeight: 'bold', color: primaryColor }}>
                {card.title}
              </div>

              {/* 描述 */}
              <div
                style={{
                  fontSize: 28,
                  color: secondaryColor,
                  marginTop: 20,
                  textAlign: 'center',
                }}
              >
                {card.description ?? cardDescription}
              </div>
            </div>
          );
        })}
      </div>

      {/* 底部文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          fontSize: 32,
          color: primaryColor,
          textAlign: 'center',
          filter: `blur(${blurAmount * 0.5}px)`,
          opacity: interpolate(revealProgress, [0.5, 1], [0, 1]),
        }}
      >
        {subtitle}
      </div>

      {/* 参数显示 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          fontSize: 24,
          color: hintColor,
          textAlign: 'center',
        }}
      >
        {resolvedTechInfo}
      </div>
    </AbsoluteFill>
  );
};
